import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatStepCount } from "./utils";

/**
 * Indented container that timeline rows render inside. Rail steps draw their
 * own line segments and nodes; prose messages sit flat between the segments.
 */
export const TIMELINE_RAIL_CLASS = "ml-[9px] space-y-0.5 py-1 pl-4";

interface AgentWorkTimelineProps {
  agentName: string;
  isActive: boolean;
  stepCount: number;
  durationLabel: string | null;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

/**
 * Collapsible per-turn work section. The header shimmers while the turn is
 * active and settles to a muted "<agent> worked for Xm · N steps" summary. It
 * names no step: a raw tool name means nothing to a customer. The expanded body
 * renders intermediate agent messages as flat prose, with the folded work
 * groups running along vertical rail segments between them.
 */
export function AgentWorkTimeline({
  agentName,
  isActive,
  stepCount,
  durationLabel,
  isExpanded,
  onToggle,
  children,
}: AgentWorkTimelineProps) {
  const headerLabel = isActive
    ? `${agentName} is working…`
    : `${agentName} worked${durationLabel ? ` for ${durationLabel}` : ""} · ${formatStepCount(stepCount)}`;

  return (
    <div className="pl-11">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex max-w-full items-center gap-2 rounded-md py-2 pr-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <ChevronRight
          className={cn(
            "h-[18px] w-[18px] shrink-0 transition-transform duration-200",
            isExpanded && "rotate-90",
          )}
        />
        <span
          className={cn("min-w-0 truncate", isActive && "animate-text-shimmer")}
        >
          {headerLabel}
        </span>
      </button>
      {isExpanded && <div className={TIMELINE_RAIL_CLASS}>{children}</div>}
    </div>
  );
}
